import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { STATE_STYLE } from "@/components/StateBadge";
import { Warehouse, Package, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminBodega() {
  const [enBodega, setEnBodega] = useState([]);
  const [lotes, setLotes] = useState([]);

  const load = () => {
    api.get("/orders?state=en_bodega").then((r) => setEnBodega(r.data));
    api.get("/batches").then((r) => setLotes(r.data));
  };
  useEffect(() => { load(); }, []);

  const abiertos = lotes.filter((l) => l.state === "abierto");
  const cerrados = lotes.filter((l) => l.state === "cerrado");

  const eliminarLote = async (l) => {
    if (l.order_ids.length > 0) {
      toast.error("El lote tiene pedidos. No se puede eliminar."); return;
    }
    if (!confirm(`¿Eliminar lote vacío de ${l.cliente_name}?`)) return;
    try {
      await api.delete(`/batches/${l.id}`);
      toast.success("Lote eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Layout>
      <div className="mb-8">
        <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-2">/ Bodega</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Gestión de bodega</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="bg-zinc-900 border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <Package className="w-5 h-5 text-yellow-300" />
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">En bodega</span>
          </div>
          <div className="font-display font-black text-5xl tracking-tighter">{enBodega.length}</div>
          <div className="font-mono-tech text-xs text-zinc-500 mt-2">Listos para despacho</div>
        </div>
        <div className="bg-zinc-900 border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <Warehouse className="w-5 h-5 text-yellow-300" />
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">Lotes abiertos</span>
          </div>
          <div className="font-display font-black text-5xl tracking-tighter">{abiertos.length}</div>
          <div className="font-mono-tech text-xs text-zinc-500 mt-2">En terreno</div>
        </div>
        <div className="bg-zinc-900 border border-white/10 p-6">
          <div className="flex items-center justify-between mb-4">
            <Warehouse className="w-5 h-5 text-yellow-300" />
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">Lotes cerrados</span>
          </div>
          <div className="font-display font-black text-5xl tracking-tighter">{cerrados.length}</div>
          <div className="font-mono-tech text-xs text-zinc-500 mt-2">Total histórico</div>
        </div>
      </div>

      {/* Lotes abiertos */}
      <div className="mb-10">
        <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-widest mb-4">/ Lotes abiertos</div>
        <div className="grid md:grid-cols-2 gap-4">
          {abiertos.map((l) => (
            <div key={l.id} className="border border-yellow-300/40 bg-yellow-300/5 p-5" data-testid={`lote-abierto-${l.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest">{l.tipo === "retiro" ? "Retiro" : "Reparto"}</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono-tech text-[10px] text-yellow-300 uppercase tracking-widest">Abierto</span>
                  {l.order_ids.length === 0 && (
                    <button
                      onClick={() => eliminarLote(l)}
                      className="w-6 h-6 border border-white/10 hover:border-red-500/50 hover:text-red-400 inline-flex items-center justify-center"
                      title="Eliminar lote vacío"
                      data-testid={`delete-lote-${l.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <div className="font-display font-bold text-xl mb-1">{l.cliente_name}</div>
              <div className="font-mono-tech text-xs text-zinc-400">{l.order_ids.length} pedidos · {new Date(l.opened_at).toLocaleString("es-CL")}</div>
              {l.order_ids.length === 0 && (
                <div className="mt-2 text-[10px] font-mono-tech text-orange-400 uppercase tracking-widest">Vacío — pendiente escaneo del repartidor</div>
              )}
            </div>
          ))}
          {abiertos.length === 0 && <div className="text-zinc-500 text-sm font-mono-tech">Sin lotes abiertos</div>}
        </div>
      </div>

      {/* Pedidos en bodega */}
      <div className="border border-white/10">
        <div className="p-4 border-b border-white/10 font-mono-tech text-xs text-zinc-500 uppercase tracking-widest">/ Pedidos en bodega — pendientes de despacho</div>
        <table className="w-full">
          <thead className="bg-zinc-900">
            <tr className="text-left text-[10px] font-mono-tech uppercase tracking-widest text-zinc-500">
              <th className="px-4 py-3">Tracking</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Destinatario</th>
              <th className="px-4 py-3">Comuna</th>
              <th className="px-4 py-3">Ingreso a bodega</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {enBodega.map((o) => (
              <tr key={o.id} className="hover:bg-zinc-900/60">
                <td className="px-4 py-3 font-mono-tech text-sm">{o.tracking_number}</td>
                <td className="px-4 py-3 text-sm">{o.cliente_name || "-"}</td>
                <td className="px-4 py-3 text-sm">{o.destinatario?.nombre}</td>
                <td className="px-4 py-3 text-sm text-zinc-400">{o.destinatario?.comuna}</td>
                <td className="px-4 py-3 font-mono-tech text-xs text-zinc-400">{new Date(o.updated_at).toLocaleString("es-CL")}</td>
              </tr>
            ))}
            {enBodega.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500 font-mono-tech text-sm">Sin pedidos en bodega</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
